import { useSyncExternalStore } from 'react';
import { useSessionOrNull } from '~/contexts/SessionContext';

/** Stable no-op subscribe used when there is no active session. */
const NOOP_SUBSCRIBE = (): (() => void) => () => {};

/**
 * Monotonic version of the session's {@link DomainStore}, bumped once per
 * committed changeset. Returns 0 when there is no session.
 *
 * Use it as a `useMemo` dependency wherever a derivation reads live domain
 * entities (whose mutations do not change any Recoil atom reference) but a
 * full projection hook would be overkill. This is the domain-driven
 * replacement for hand-rolled counters such as the former
 * `imageListVersionState`, which had to be bumped manually at every call
 * site and was silently wrong wherever someone forgot.
 */
export function useDomainVersion(): number {
    const session = useSessionOrNull();
    const store = session?.context.store;
    return useSyncExternalStore(
        store?.subscribe ?? NOOP_SUBSCRIBE,
        () => store?.getVersion() ?? 0
    );
}
